/*eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Label, Modal, ModalBody, Row, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { useDispatch } from 'react-redux';
import { t } from 'i18next';

import { JobRole, PAGE_SIZE_OPTIONS, USER_ROLE } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import LocationService from '../../services/Location';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';

import SortableHeader from '../../components/SortableHeader';
import NoRecords from '../../components/Table/NoRecords';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';

import useListPage from '../../hooks/useListPage';

const HeaderList = [
  {
    title: '',  // Edit button column (RIGHT side in RTL)
    value: 'editAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
  {
    title: t('LOCATION.BUSINESS_NAME_LABEL'),
    value: 'business',
    style: { minWidth: '150px', width: '25%' },
  },
  {
    title: t('MACHINE.NUMBER'),
    value: 'terminalNumber',
    style: { minWidth: '90px', maxWidth: '120px', width: '10%' },  // Reduced: max 15 chars
  },
  {
    title: t('EMPLOYEE.CITY_LABEL'),
    value: 'city',
    style: { minWidth: '100px', maxWidth: '150px', width: '12%' },  // Reduced: max 25 chars
  },
  {
    title: t('EMPLOYEE.STREET_LABEL'),
    value: 'street',
    style: { minWidth: '130px', width: '18%' },  // Keep as is
  },
  {
    title: t('LOCATION.HOUSE_NUMBER_LABEL'),
    value: 'houseNumber',
    style: { minWidth: '60px', maxWidth: '80px', width: '6%' },  // Reduced: max 5 chars
  },
  {
    title: t('EMPLOYEE.ZIPCODE_LABEL'),
    value: 'zipCode',
    style: { minWidth: '80px', width: '8%' },
  },
  {
    title: t('LOCATION.MODEM_NUMBER_LABEL'),  // New column
    value: 'modemNumber',
    style: { minWidth: '90px', maxWidth: '120px', width: '10%' },
  },
  {
    title: '',  // Delete button column (LEFT side in RTL)
    value: 'deleteAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
];

function LocationList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [setPageTitle, setBreadcrumbItems] = useOutletContext();

  const [roleID, setRoleID] = useState('');
  const [showDelRec, setShowDelRec] = useState(false);
  const showDelRecRef = useRef(false);

  // Delete & activate modal state (custom flow, not using hook's delete)
  const [deleteItem, setDeleteItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [deleteLocationModal, setDeleteLocationModal] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [confDelTerm, setConfDelTerm] = useState({
    shown: false,
    confirmDelTermFn: () => { },
  });

  const {
    list,
    isLoading,
    totalCount,
    currentPage,
    itemsPerPage,
    fetchList,
    debouncedSearch,
    sortBy,
    handlePageClick,
    handlePageSizeChange,
  } = useListPage({
    service: LocationService,
    listMethod: 'listLocation',
    // No deleteMethod — Location has a custom multi-step delete flow
    initialSort: { value: 'business', asc: true },
    initialPageSize: 10,
    buildPayload: (payload) => {
      payload.showDelRec = showDelRecRef.current;
    },
  });

  useEffect(() => {
    setPageTitle(t('LOCATION.TITLE'));
    setBreadcrumbItems([
      { title: t('APPLICATION_TITLE'), link: '#' },
      { title: t('LOCATION.TITLE'), link: '#' },
    ]);

    const jobRole = LocalstorageService.getItem(JobRole);
    setRoleID(jobRole);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDeleteModal = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const resetModal = () => {
    setDeleteItem(null);
    setShowDeleteModal(false);
    setShowActivateModal(false);
  };

  const handleCancleLocationDelete = async () => {
    const DeleteTerminal = false;
    const payload = { id: deleteItem.id, terminalNumber: deleteItem.terminalNumber, DeleteTerminal };

    setIsDeleteLoading(true);
    await LocationService.deleteLocation(payload)
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        } else if (status === 201) {
          return;
        }

        toast.success(message);
        fetchList();
      })
      .finally(() => {
        setIsDeleteLoading(false);
        setDeleteLocationModal(false);
      });
  };

  const confirmDelete = async (e, DeleteTerminal = true) => {
    const payload = { id: deleteItem.id, terminalNumber: deleteItem.terminalNumber, DeleteTerminal };

    let flag = false;
    setIsDeleteLoading(true);
    await LocationService.deleteLocation(payload)
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        } else if (status === 201) {
          flag = true;
          setConfDelTerm({
            shown: true,
            message,
          });
          return;
        }

        toast.success(message);
        fetchList();
      })
      .finally(() => {
        setIsDeleteLoading(false);
        !flag && setConfDelTerm({ shown: false, confirmDelTermFn: () => { } });
      });

    flag ? setShowDeleteModal(false) : resetModal();
  };

  const editFn = (item) => {
    navigate(`../update/${item.id}`, {
      state: { companyId: item.companyId, terminalNumber: item.terminalNumber, locationId: item.id },
    });
  };

  const viewFn = (item) => {
    navigate(`../update/${item.id}`, { state: { companyId: item.companyId } });
  };

  const handleShowDeleteRec = () => {
    const newShowDelRec = !showDelRec;
    setShowDelRec(newShowDelRec);
    showDelRecRef.current = newShowDelRec;
    // Reset to page 1 and re-fetch (buildPayload reads from ref)
    handlePageClick(1);
  };

  const confirmActivate = async () => {
    dispatch(setLoadingOverlay(true));
    await LocationService.activateLocation({
      id: deleteItem.id,
      companyId: deleteItem.companyId,
    })
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        toast.success(message);
        fetchList();
      })
      .finally(() => {
        dispatch(setLoadingOverlay(false));
      });

    resetModal();
  };

  return (
    <React.Fragment>
      <div className="align-items-end mb-2">
        <Row>
          <Col xs={7} sm={4}>
            <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
              <InputGroup>
                <Input onChange={debouncedSearch} placeholder={t('SEARCH')} />
                <Button color="primary" type="button">
                  <i className="ri-search-line"></i>
                </Button>
              </InputGroup>
            </Form>
          </Col>
          <Col xs={5} sm={4} className="d-none d-sm-block">
            {(roleID === USER_ROLE.SUPER_ADMIN || roleID === USER_ROLE.COMPANY_ADMIN) && (
              <FormGroup className="float-end pt-2" check>
                <Input
                  id="showDeleteRec"
                  title={t('SHOW_DEL_REC')}
                  type="checkbox"
                  onChange={handleShowDeleteRec}
                  checked={showDelRec}
                />
                <Label htmlFor="showDeleteRec" className="mb-0 d-sm-block">
                  {t('SHOW_DEL_REC')}
                </Label>
              </FormGroup>
            )}
          </Col>
          <Col xs={5} sm={4}>
            {roleID !== USER_ROLE.EMPLOYEE && (
              <Button color="primary" className="float-end" onClick={() => navigate('../add')}>
                <span className="d-none d-sm-block">{t('LOCATION.ADD')}</span>
                <i className="ri-add-line d-sm-none" title={t('LOCATION.ADD')}></i>
              </Button>
            )}
          </Col>
        </Row>
        <Row className="d-sm-none">
          <Col xs={8} sm={4} className="mt-2">
            {(roleID === USER_ROLE.SUPER_ADMIN || roleID === USER_ROLE.COMPANY_ADMIN) && (
              <FormGroup className="" check>
                <Input
                  id="showDeleteRecMobile"
                  title={t('SHOW_DEL_REC')}
                  type="checkbox"
                  onChange={handleShowDeleteRec}
                  checked={showDelRec}
                />
                <Label htmlFor="showDeleteRecMobile" className="mb-0 d-sm-block">
                  {t('SHOW_DEL_REC')}
                </Label>
              </FormGroup>
            )}
          </Col>
        </Row>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader headers={HeaderList} isDisabled={list.length === 0} sortClick={sortBy} showActionCol={false} />
          </tr>
        </thead>

        <tbody>
          {list.map((item, l0Idx) => (
            <tr key={l0Idx}>
              {/* Edit button column - RIGHT side in RTL */}
              <td className="text-center">
                {roleID === USER_ROLE.EMPLOYEE ? (
                  <Button color="primary" size="sm" title={t('LOCATION.VIEW')} onClick={() => viewFn(item)}>
                    {t('LOCATION.VIEW')}
                  </Button>
                ) : showDelRec ? (
                  <Button
                    color="success"
                    size="sm"
                    title={t('ACTIVATE')}
                    onClick={() => {
                      setDeleteItem(item);
                      setShowActivateModal(true);
                    }}
                  >
                    {t('ACTIVATE')}
                  </Button>
                ) : (
                  <Button color="primary" size="sm" title={t('LOCATION.UPDATE')} onClick={() => editFn(item)}>
                    {t('LOCATION.EDIT_BTN')}
                  </Button>
                )}
              </td>
              <td>{item.locationName}</td>
              <td>{item.terminalNumber}</td>
              <td>{item.city}</td>
              <td>{item.street}</td>
              <td>{item.houseNumber}</td>
              <td>{item.zip}</td>
              <td>{item.modemNumber || ''}</td>
              {/* Delete button column - LEFT side in RTL */}
              <td className="text-center">
                {roleID !== USER_ROLE.EMPLOYEE && !showDelRec && (
                  <Button color="danger" size="sm" title={t('LOCATION.DELETE')} onClick={() => openDeleteModal(item)}>
                    {t('LOCATION.DELETE_BTN')}
                  </Button>
                )}
              </td>
            </tr>
          ))}

          {!isLoading && list.length === 0 && <NoRecords colSpan={HeaderList.length} />}
        </tbody>
      </Table>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={itemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <Modal isOpen={confDelTerm.shown} backdrop="static" className="modal-dialog-centered">
        <ModalBody>
          <div className="d-flex justify-content-center align-items-center mt-3">
            <h3 className="mx-2 mb-0">{confDelTerm.message}</h3>
          </div>

          <br className="d-none d-sm-block" />
          <br />
          <div className="d-flex justify-content-end gap-2">
            <Button color="primary" onClick={() => setConfDelTerm({ shown: false })}>
              {t('CANCEL')}
            </Button>

            <Button
              color="link"
              className="waves-effect me-1"
              onClick={(e) => confirmDelete(e, true)}
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? t('PLEASE_WAIT') : t('OK')}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmDeleteModal
        isOpen={showActivateModal}
        isLoading={isDeleteLoading}
        message={t('ALERT.ACT_LOCATION')}
        modalSize=""
        confirmFn={confirmActivate}
        cancelFn={resetModal}
        btnName={t('ACTIVATE')}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        isLoading={isDeleteLoading}
        message={t('LOCATION.LOCATION_DELETE_MODAL_MESSAGE')}
        confirmFn={() => {
          setDeleteLocationModal(true);
          setShowDeleteModal(false);
        }}
        cancelFn={resetModal}
      />

      <ConfirmDeleteModal
        isOpen={deleteLocationModal}
        message={t('LOCATION.LOCATION_TERMINAL_DELETE_MODAL_MESSAGE')}
        isLoading={isDeleteLoading}
        confirmFn={confirmDelete}
        cancelFn={handleCancleLocationDelete}
      />
    </React.Fragment>
  );
}

export default LocationList;
